import { useState, useEffect, useRef } from "react";
import type { NavigationProps, NavItemButton, NavItemLink, NavSection } from "@wayflyer/flyui";
import {
  IconCity20Line,
  IconGift20Line,
  IconUserPlus20Line,
  IconSettings20Line,
  IconLogOut20Line,
  IconLotOfCash20Line,
  IconReports20Line,
  IconCreditCard20Line,
  IconGraphUp20Line,
  IconDollarCircle20Line,
  IconConnections20Line,
  IconOpenBook20Line,
  IconBank20Line,
  IconEmptyPage20Line,
  IconGroup20Line,
  IconHelpCircle20Line,
  IconWrench20Line,
  IconCheckSquares20Line,
  IconHeadsetHelp20Line,
  IconHome20Line,
} from "@wayflyer/flyui-icons/20/line";
import { BaseSchemas } from "api-types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import type { NavigateOptions } from "react-router-dom";
import { useInviteModalActions } from "shared-ui";

import { MAX_VISIBLE_ACCOUNTS } from "../../../banking/constants";
import { useIntercomLoaded } from "../intercom";

import { getNavItemNameFromId } from "./nav-item-strings";
import { useNavTaskNotificationCount } from "./nav-task-notification-count";

declare global {
  interface Window {
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
  }
}
function getIconComponent(icon: string): ReactNode | undefined {
  if (typeof icon === "string") {
    switch (icon) {
      case "home":
        return <IconHome20Line />;
      case "bank":
        return <IconLotOfCash20Line />;
      case "line-chart":
        return <IconReports20Line />;
      case "team":
        return <IconUserPlus20Line />;
      case "help":
        return <IconHelpCircle20Line />;
      case "connections":
        return <IconConnections20Line />;
      case "presentation-chart-line":
        return <IconReports20Line />;
      case "benchmarks":
        return <IconGraphUp20Line />;
      case "banking":
        return <IconCity20Line />;
      case "financial-os":
        return <IconGraphUp20Line />;
      case "internal":
        return <IconWrench20Line />;
      case "dollar":
        return <IconDollarCircle20Line />;
      case "share":
        return <IconGift20Line />;
      case "account":
        return <IconBank20Line />;
      case "payments":
        return <IconGraphUp20Line />;
      case "recipients":
        return <IconGroup20Line />;
      case "cards":
        return <IconCreditCard20Line />;
      case "transactions":
        return <IconOpenBook20Line />;
      case "documents":
        return <IconEmptyPage20Line />;
      case "tasks":
        return <IconCheckSquares20Line />;
      default:
        return;
    }
  }
}

const navItemMatches = (path: string, alternativePaths: string[] = [], pathname: string): boolean => {
  if (path === pathname || alternativePaths.includes(pathname)) {
    return true;
  } else {
    return alternativePaths.some((alternativePath) => pathname.startsWith(alternativePath));
  }
};

export const useNavigationItems = ({
  appConfig,
  clientNavigateFn,
}: {
  appConfig: BaseSchemas.CustomerAppConfig;
  clientNavigateFn: (path: string, options?: NavigateOptions) => void;
}): NavigationProps => {
  const intl = useIntl();
  const { navItems, customerDomain } = appConfig;
  const taskNotifications = useNavTaskNotificationCount(customerDomain);
  const enableClientSideLinks = useFeatureFlagEnabled("enable-all-client-side-customer-app-links");
  const { openInviteModal } = useInviteModalActions();
  const alternativePathsMap = {};
  const [messageCount, setMessageCount] = useState(0);
  const messengerOpen = useRef(false);
  const isIntercomLoaded = useIntercomLoaded();

  useEffect(() => {
    if (isIntercomLoaded) {
      const handleUnreadCountChange = (unreadCount: number) => {
        setMessageCount(unreadCount);
      };
      const handleHide = () => {
        messengerOpen.current = false;
      };
      const handleShow = () => {
        messengerOpen.current = true;
      };

      window.Intercom("onUnreadCountChange", handleUnreadCountChange);
      window.Intercom("onHide", handleHide);
      window.Intercom("onShow", handleShow);

      return () => {
        // Clean up listeners
        window.Intercom("off", "onUnreadCountChange", handleUnreadCountChange);
        window.Intercom("off", "onHide", handleHide);
        window.Intercom("off", "onShow", handleShow);
      };
    }
  }, [isIntercomLoaded]);

  const linkBehavior = enableClientSideLinks
    ? {}
    : {
        navigateFn: clientNavigateFn,
      };

  for (const navItem of navItems ?? []) {
    if (navItem.alternativePaths) {
      alternativePathsMap[navItem.path] = navItem.alternativePaths;
    }

    if (navItem.subItems) {
      for (const subItem of navItem.subItems) {
        if (subItem.alternativePaths) {
          alternativePathsMap[subItem.path] = subItem.alternativePaths;
        }
      }
    }
  }

  const getIsRouteSelected = (pathname: string): boolean => {
    return navItemMatches(pathname, alternativePathsMap[pathname], location.pathname);
  };

  function getClientSubNavItems(
    navItems: BaseSchemas.CustomerAppConfig["navItems"] | null | undefined
  ): Array<NavItemButton | NavItemLink> | undefined {
    if (!navItems || navItems.length === 0) {
      return;
    }

    return navItems.map(({ id, icon, path }) => {
      return {
        id,
        href: path,
        icon: getIconComponent(icon),
        label: getNavItemNameFromId(intl, id),
        ...linkBehavior,
      };
    });
  }

  const userSection: NavigationProps["userSection"] = {
    label: appConfig.customerName?.length > 0 ? appConfig.customerName : appConfig.userName,
    id: "user-section",
    icon: null,
    items: [
      ...(appConfig.customerName?.length > 0
        ? [
            {
              id: "settings",
              label: intl.formatMessage({ id: "Menu: Settings", defaultMessage: "Settings" }),
              href: `/${appConfig?.customerDomain}/settings`,
              icon: <IconCity20Line />,
              ...linkBehavior,
            },
            {
              id: "invite",
              label: intl.formatMessage({ id: "Invite someone", defaultMessage: "Invite a team member" }),
              onClick: () => openInviteModal(),
              icon: <IconGroup20Line />,
            },
          ]
        : []),
      {
        id: "help",
        label: intl.formatMessage({ id: "help centre title", defaultMessage: "Help center" }),
        href: "https://www.wayflyer.com/help-center",
        external: true,
        icon: <IconHelpCircle20Line />,
      },
      ...(window?.Optanon?.ToggleInfoDisplay
        ? [
            {
              id: "privacy-settings",
              label: intl.formatMessage({
                id: "Privacy settings",
                defaultMessage: "Privacy settings",
              }),
              onClick: () => window.Optanon.ToggleInfoDisplay(),
              icon: <IconSettings20Line />,
            },
          ]
        : []),
      {
        id: "sign-out",
        label: intl.formatMessage({ id: "Sign out", defaultMessage: "Sign out" }),
        error: true,
        href: "/authn/logout",
        reloadDocument: enableClientSideLinks ? true : undefined,
        icon: <IconLogOut20Line />,
      },
    ],
  };

  const NAVIGABLE_SECTIONS = ["banking/payments", "banking/accounts"];

  const toClientNavItem = (navItem: BaseSchemas.NavItem): NavItemLink | NavSection => {
    const { id } = navItem;
    let items = getClientSubNavItems(navItem.subItems);
    const icon = getIconComponent(navItem.icon);
    const label = getNavItemNameFromId(intl, navItem.id);
    const navigableSection = navItem.path && NAVIGABLE_SECTIONS.some((section) => navItem.path.includes(section));

    const isAccountsSection = id.includes("accounts") || navItem.path?.includes("/accounts");

    if (isAccountsSection && Array.isArray(items) && items.length > 0) {
      if (items.length > MAX_VISIBLE_ACCOUNTS) {
        const visibleItems = items.slice(0, MAX_VISIBLE_ACCOUNTS);
        const remainingCount = items.length - MAX_VISIBLE_ACCOUNTS;

        const seeMoreItem: NavItemLink = {
          id: "banking-accounts-see-more",
          label: `See more (${remainingCount})`,
          href: "#",
          icon: undefined,
          navigateFn: () => {
            const accountsPath = navItem.path || "";
            clientNavigateFn(`${accountsPath}?expand=true`);
          },
        };

        items = [...visibleItems, seeMoreItem];
      }

      if (items.length === 1) {
        const singleAccountItem = items[0];
        const accountHref = "href" in singleAccountItem ? singleAccountItem.href : navItem.path;
        return {
          id,
          icon,
          label,
          href: accountHref,
          ...linkBehavior,
        };
      }
    }

    if (items) {
      return {
        id,
        icon,
        label,
        items,
        ...(navigableSection && {
          href: navItem.path,
          ...linkBehavior,
        }),
      };
    } else {
      return {
        id,
        icon,
        label,
        href: navItem.path,
        ...linkBehavior,
      };
    }
  };

  const sections: NavigationProps["sections"] = {
    navItems: (navItems ?? []).map(toClientNavItem).filter((item) => !item.href?.includes("customer-referrals")), //@todo: do this on the server,
  };

  const infoItems = [
    {
      id: "support",
      label: intl.formatMessage({ id: "support title", defaultMessage: "Support" }),
      onClick: () => {
        if ("Intercom" in window) {
          if (messengerOpen.current) {
            window.Intercom("hide");
          } else {
            window.Intercom("show");
          }
        }
      },
      icon: <IconHeadsetHelp20Line />,
    },
    ...(navItems ?? []).map(toClientNavItem).filter((item) => item.href?.includes("customer-referrals")), //@todo: do this on the server,
  ] as Array<NavItemLink>;

  const notificationBubble =
    messageCount > 0
      ? {
          navItemId: "support",
          count: messageCount,
          title: `${messageCount} messages require your attention`,
        }
      : taskNotifications > 0
      ? {
          navItemId: "customer tasks page title",
          count: taskNotifications,
          title: `${taskNotifications} tasks require your attention`,
        }
      : undefined;

  return {
    userSection,
    sections,
    infoItems,
    getIsRouteSelected,
    notificationBubble,
  };
};
